import { Box, Stack, Typography } from '@mui/material'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { useBackendLocales } from '../contexts/backendLocaleContext'
import { getUnansweredCurrentMandatoryFilters, useFilterContext } from '../contexts/filterContext'
import useBreakpoints from '../hooks/useBreakpoints'
import HyButton from './common/hy/HyButton'
import HyTag from './common/hy/HyTag'
import { hy } from './common/hy/hyTokens'
import ResetFiltersButton from './ResetFiltersButton'

type UnansweredPromptProps = {
  filters: { id: string; shortName?: string }[]
  onOpenFilters: () => void
  onHighlightFilter: (filterId: string) => void
}

const UnansweredPrompt = ({ filters, onOpenFilters, onHighlightFilter }: UnansweredPromptProps) => {
  const { t } = useTranslation()
  const promptId = useId()
  return (
    <Box sx={{ mb: 3 }}>
      <Typography id={promptId} variant="body1" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('v2:noRecommendations.unansweredMandatory')}:
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} role="group" aria-labelledby={promptId}>
        {filters.map(filter => (
          <HyTag
            key={filter.id}
            text={filter.shortName ?? filter.id}
            colour="attention"
            onClick={() => {
              onOpenFilters()
              onHighlightFilter(filter.id)
            }}
            ariaLabel={t('v2:noRecommendations.openFilterQuestion', { question: filter.shortName ?? filter.id })}
          />
        ))}
      </Stack>
    </Box>
  )
}

type NoRecommendationsInfoProps = {
  onOpenFilters: () => void
}

const NoRecommendationsInfo = ({ onOpenFilters }: NoRecommendationsInfoProps) => {
  const { t } = useTranslation()
  const { isDrawerLayout } = useBreakpoints()
  const filterContext = useFilterContext()
  const { filters, setHighlightedFilterId } = filterContext
  const { renderLocale } = useBackendLocales()

  const mandatoryFilters = getUnansweredCurrentMandatoryFilters(filters, filterContext)

  return (
    <Box
      sx={{
        padding: { xs: 2, sm: 2.5 },
        border: '1px solid',
        borderColor: hy.borderColor.light,
        backgroundColor: hy.bgColor.white,
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.25 }} alignItems="flex-start">
        <Typography
          variant="h3"
          component="h2"
          data-testid="no-recommendations-heading"
          sx={{ fontSize: { xs: 'h4.fontSize', sm: 'h3.fontSize' } }}
        >
          {t('v2:noRecommendations.title')}
        </Typography>
        {renderLocale('noRecommendations.additionalInfo')}
        {mandatoryFilters.length > 0 ? (
          <UnansweredPrompt
            filters={mandatoryFilters}
            onOpenFilters={onOpenFilters}
            onHighlightFilter={setHighlightedFilterId}
          />
        ) : (
          <Stack direction="column" spacing={'8px'} flexWrap="wrap">
            {isDrawerLayout && (
              <HyButton variant="primary" colour="blue" onClick={onOpenFilters}>
                {t('v2:noRecommendations.changeSelectionsButton')}
              </HyButton>
            )}
            <ResetFiltersButton onReset={onOpenFilters} dataTestId="empty-state-clear-choices" />
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

export default NoRecommendationsInfo
